import PushPreferences from './features/notifications/PushPreferences';
import SettingsPage from './features/settings/SettingsPage';
import BlockedUsersPage from './features/settings/BlockedUsersPage';
import ProfileEditPage from './features/profile/ProfileEditPage';
import ProfileVisibilityPage from './features/profile/ProfileVisibilityPage';
import BadgePage from './features/badges/BadgePage';
import Sponsorship from './pages/sponsorship/Sponsorship';
import React, { useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { HelmetProvider } from 'react-helmet-async';
import { SiteMetadata } from './components/common/SEO';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from './queryClient';
import MusicGenreCatalogLifecycle from './features/taste/MusicGenreCatalogLifecycle';
import { createPostpopTheme } from './theme';
import Layout from './components/layout/Layout';
import Explore from './pages/explore/Explore';
import Timeline from './pages/explore/Timeline';
import SearchHome from './pages/search/SearchHome';
import { WebLocaleProvider } from './contexts/WebLocaleContext';
import AppContent from './AppContent';
import { createBrowserRouter, Navigate, useLocation, useParams } from 'react-router-dom';
import Login from './pages/login/Login';
import AppleWebReturn from './features/auth/AppleWebReturn';
import NewsForm from './pages/news/NewsForm';
import Albums from './pages/album/Albums';
import Reviews from './pages/review/Reviews';
import Playlists from './pages/playlist/Playlists';
import PlaylistDetail from './pages/playlist/PlaylistDetail';
import Notice from './pages/notice/Notice';
import About from './pages/about/About';
import Contact from './pages/Contact';
import Home from './pages/home/Home';
import Signup from './pages/login/Signup';
import SignupDetail from './pages/login/SignupDetail';
import PasswordForgot from './pages/login/PasswordForgot';
import PasswordReset from './pages/login/PasswordReset';
import PasswordChange from './pages/login/PasswordChange';
import LoginSuccess from './pages/login/LoginSuccess';
import LoginError from './pages/login/LoginError';
import NoticeDetail from './pages/notice/NoticeDetail';
import FeedDetail from './pages/feed/FeedDetail';
import FeedList from './pages/feed/FeedList';
import ReviewDetail from './pages/review/ReviewDetail';
import NoticeForm from './pages/notice/NoticeForm';
import PlaylistCreate from './pages/playlist/PlaylistCreate';
import PlaylistEdit from './pages/playlist/PlaylistEdit';
import MusicEditionPage from './features/musicEdition/MusicEditionPage';
import Mypage from './pages/mypage/Mypage';
import TermsOfUse from './pages/about/TermsOfUse';
import PrivacyPolicy from './pages/about/PrivacyPolicy';
import AccountDeletion from './pages/AccountDeletion';
import Admin from './pages/admin/Admin';
import BannerForm from './pages/admin/components/BannerForm';
import SearchResults from './pages/search/SearchResults';
import SearchResultDetail from './pages/search/SearchResultDetail';
import ArtistDetail from './pages/artist/ArtistDetail';
import ArtistViewAll from './pages/artist/ArtistViewAll';
import NotFound from './pages/NotFound';
import AdminRoute from './AdminRoute';
import UserPage from './pages/users/UserPage';
import UserDiscoveryPage from './features/members/UserDiscoveryPage';
import { ErrorProvider } from './contexts/ErrorContext';
import { ColorSchemeProvider, useColorScheme } from './contexts/ColorSchemeContext';
import ReviewUpload from './pages/review/ReviewUpload';
import Notifications from './pages/notifications/Notifications';
import CriticReviewForm from './pages/review/components/critic-review/CriticReviewForm';
import UserReviews from './pages/review/UserReviews';
import UserReview from './pages/review/UserReview';
import RelatedAlbumsViewAll from './pages/review/RelatedAlbumsViewAll';

const LegacyMixesRedirect = ({ variant }) => {
  const location = useLocation();
  const params = useParams();
  const search = location.search || '';

  const target =
    variant === 'create'
      ? `/lists/create${search}`
      : variant === 'edit'
        ? `/lists/${params.id}/edit${search}`
        : variant === 'comment'
          ? `/lists/${params.slug}/comment/${params.commentId}${search}`
          : variant === 'detail'
            ? `/lists/${params.slug}${search}`
            : `/lists${search}`;

  return <Navigate to={target} replace />;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/explore', element: <Explore /> },
      { path: '/explore/timeline', element: <Timeline /> },
      { path: '/search', element: <SearchHome /> },
      {
        path: '/news/create',
        element: (
          <AdminRoute>
            <NewsForm />
          </AdminRoute>
        ),
      },
      {
        path: '/news/:id/edit',
        element: (
          <AdminRoute>
            <NewsForm />
          </AdminRoute>
        ),
      },
      { path: '/albums', element: <Albums /> },
      {
        path: '/albums/upload',
        element: (
          <AdminRoute>
            <ReviewUpload />
          </AdminRoute>
        ),
      },
      { path: '/reviews', element: <Reviews /> },
      {
        path: '/reviews/upload',
        element: (
          <AdminRoute>
            <ReviewUpload />
          </AdminRoute>
        ),
      },
      { path: '/reviews/edit', element: <Reviews /> },
      {
        path: '/reviews/:albumSource/:sourceId/critic-reviews/create',
        element: (
          <AdminRoute>
            <CriticReviewForm />
          </AdminRoute>
        ),
      },
      {
        path: '/reviews/:albumSource/:sourceId/critic-reviews/:reviewId/edit',
        element: (
          <AdminRoute>
            <CriticReviewForm />
          </AdminRoute>
        ),
      },
      { path: '/reviews/:albumSource/:sourceId/related-albums', element: <RelatedAlbumsViewAll /> },
      { path: '/reviews/:albumSource/:sourceId/user-reviews', element: <UserReviews /> },
      { path: '/reviews/:albumSource/:sourceId/user-reviews/:reviewId', element: <UserReview /> },
      {
        path: '/reviews/:albumSource/:sourceId/user-reviews/:reviewId/comment/:commentId',
        element: <UserReview />,
      },
      { path: '/reviews/:albumSource/:sourceId', element: <ReviewDetail /> },
      { path: '/reviews/:albumSource/:sourceId/comment/:commentId', element: <ReviewDetail /> },
      // 슬러그 기반 리뷰 라우트 (새 URL)
      {
        path: '/reviews/:albumSlug/critic-reviews/create',
        element: (
          <AdminRoute>
            <CriticReviewForm />
          </AdminRoute>
        ),
      },
      {
        path: '/reviews/:albumSlug/critic-reviews/:reviewId/edit',
        element: (
          <AdminRoute>
            <CriticReviewForm />
          </AdminRoute>
        ),
      },
      { path: '/reviews/:albumSlug/related-albums', element: <RelatedAlbumsViewAll /> },
      { path: '/reviews/:albumSlug/user-reviews', element: <UserReviews /> },
      { path: '/reviews/:albumSlug/user-reviews/:reviewId', element: <UserReview /> },
      {
        path: '/reviews/:albumSlug/user-reviews/:reviewId/comment/:commentId',
        element: <UserReview />,
      },
      { path: '/reviews/:albumSlug', element: <ReviewDetail /> },
      { path: '/reviews/:albumSlug/comment/:commentId', element: <ReviewDetail /> },
      { path: '/lists', element: <Playlists /> },
      { path: '/lists/create', element: <PlaylistCreate /> },
      { path: '/lists/:id/edit', element: <PlaylistEdit /> },
      { path: '/lists/:slug', element: <PlaylistDetail /> },
      { path: '/lists/:slug/comment/:commentId', element: <PlaylistDetail /> },
      { path: '/mixes', element: <LegacyMixesRedirect /> },
      { path: '/mixes/create', element: <LegacyMixesRedirect variant="create" /> },
      { path: '/mixes/:id/edit', element: <LegacyMixesRedirect variant="edit" /> },
      { path: '/mixes/:slug', element: <LegacyMixesRedirect variant="detail" /> },
      {
        path: '/mixes/:slug/comment/:commentId',
        element: <LegacyMixesRedirect variant="comment" />,
      },
      { path: '/sponsorship', element: <Sponsorship /> },
      { path: '/sponsorship/benefits', element: <Sponsorship page="benefits" /> },
      { path: '/sponsorship/vision', element: <Sponsorship page="vision" /> },
      { path: '/sponsorship/roster', element: <Sponsorship page="roster" /> },
      { path: '/notice', element: <Notice /> },
      {
        path: '/notice/create',
        element: (
          <AdminRoute>
            <NoticeForm />
          </AdminRoute>
        ),
      },
      {
        path: '/notice/:id/edit',
        element: (
          <AdminRoute>
            <NoticeForm />
          </AdminRoute>
        ),
      },
      { path: '/notice/:slug', element: <NoticeDetail /> },
      { path: '/feed', element: <FeedList /> },
      { path: '/feed/:identifier', element: <FeedDetail /> },
      { path: '/notice/:slug/comment/:commentId', element: <NoticeDetail /> },
      { path: '/about', element: <About /> },
      { path: '/about/terms-of-use', element: <TermsOfUse /> },
      { path: '/about/privacy-policy', element: <PrivacyPolicy /> },
      { path: '/account-deletion', element: <AccountDeletion /> },
      { path: '/contact', element: <Contact /> },
      { path: '/signup', element: <Signup /> },
      { path: '/signup/detail', element: <SignupDetail /> },
      { path: 'login', element: <Login /> },
      { path: '/login/apple/return', element: <AppleWebReturn /> },
      { path: '/login/success', element: <LoginSuccess /> },
      { path: '/login/error', element: <LoginError /> },
      { path: '/password-forgot', element: <PasswordForgot /> },
      { path: '/password-reset', element: <PasswordReset /> },
      { path: '/password-change', element: <PasswordChange /> },
      { path: '/mypage', element: <Mypage /> },
      { path: '/mypage/push-settings', element: <PushPreferences /> },
      { path: '/mypage/settings', element: <SettingsPage /> },
      { path: '/mypage/blocked', element: <BlockedUsersPage /> },
      { path: '/mypage/find-members', element: <UserDiscoveryPage /> },
      { path: '/mypage/edit', element: <ProfileEditPage /> },
      { path: '/mypage/visibility', element: <ProfileVisibilityPage /> },
      { path: '/mypage/badges', element: <BadgePage /> },
      { path: '/users/:nickname/badges', element: <BadgePage /> },
      { path: '/mypage/archive', element: <MusicEditionPage /> },
      { path: '/mypage/:tab', element: <Mypage /> },
      { path: '/notifications', element: <Notifications /> },
      { path: '/users/:nickname', element: <UserPage /> },
      { path: '/users/by-id/:userId', element: <UserPage /> },
      { path: '/users/:nickname/:tab', element: <UserPage /> },
      {
        path: '/admin',
        element: (
          <AdminRoute>
            <Admin />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/banners/create',
        element: (
          <AdminRoute>
            <BannerForm />
          </AdminRoute>
        ),
      },
      {
        path: '/admin/banners/:id/edit',
        element: (
          <AdminRoute>
            <BannerForm />
          </AdminRoute>
        ),
      },
      { path: '/search/summary', element: <SearchResults /> },
      { path: '/search/:category', element: <SearchResultDetail /> },
      { path: '/artists/:artistSlug', element: <ArtistDetail /> },
      { path: '/artists/:artistSlug/view/:albumType', element: <ArtistViewAll /> },
      { path: '/artists/:artistSource/:sourceId', element: <ArtistDetail /> },
      { path: '/artists/:artistSource/:sourceId/:albumType', element: <ArtistViewAll /> },
      { path: '/not-found', element: <NotFound /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

function ThemedApp() {
  const { resolvedMode } = useColorScheme();
  const theme = useMemo(() => createPostpopTheme(resolvedMode), [resolvedMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppContent router={router} />
    </ThemeProvider>
  );
}

function App() {
  return (
    <WebLocaleProvider>
      <ColorSchemeProvider>
        <HelmetProvider>
          <SiteMetadata />
          <ErrorProvider>
            <QueryClientProvider client={queryClient}>
              <MusicGenreCatalogLifecycle />
              <ThemedApp />
            </QueryClientProvider>
          </ErrorProvider>
        </HelmetProvider>
      </ColorSchemeProvider>
    </WebLocaleProvider>
  );
}

export default App;
